<template>
  <ScipherLayout v-restore-scroll="{ data: getScrollData, fnc: loadInitialData }">
    <template #menu>
      <FixedPlaceholder :showNewCohort="showFilters" :component="CohortsLeftMenu" :selectDefault="hasSearchParameters" />
    </template>
    <div :class="{ disabled: isLoading }">
      <CohortSearchBox class="mt-4 mb-5" :value="searchFilter" :showFilters="showFilters" :isLoading="isLoading" @input="searchChanged" @create-cohort="createCohort" />
      <div />

      <div class="cohorts-container">
        <div class="mb-5 mx-4 px-3" v-if="bestCohorts.any()">
          <div class="hvh-flex space-between mt-4 pt-2">
            <div class="font-18px font-weight-bold">Best Results</div>
          </div>
          <CohortListItem class="my-3" :value="item" :availableClients="availableClients" :options="options" v-on="cohortListOns(item)" v-for="(item, ind) in bestCohorts" :key="`Best-${ind}`" />
        </div>
        <div class="mb-5 mx-4 px-3" v-if="moreCohorts.any()">
          <div class="hvh-flex space-between mt-4 pt-2">
            <div class="font-18px font-weight-bold">{{ resultSelectionTitle }}</div>
          </div>
          <CohortListItem class="my-3" :value="item" :availableClients="availableClients" :options="options" v-on="cohortListOns(item)" v-for="(item, ind) in moreCohorts" :key="`Best-${ind}`" />
          <CohortsShowMore :class="{ disabled: isLoadingRecent }" :count="searchedCohorts.length" :totalCount="countMoreBestCohorts" :isLoading="isLoadingBest" @click="getBestResults" v-if="showMoreBestCohorts" />
        </div>

        <div class="mb-5 mx-4 px-3" v-if="cohortsNotFound">
          <div style="text-align: center; color: red">No Matching Cohorts Found. Search Name, Description, Creator, Tags, or Clinical Criteria .</div>
        </div>

        <div class="mb-5 mx-4 px-3" v-if="getFlag('cohorts-show-pinned') && pinnedCohorts.any() && !cohortsNotFound">
          <div class="hvh-flex space-between mt-4 pt-2">
            <div class="font-18px font-weight-bold">Pinned Cohorts</div>
          </div>
          <CohortListItem class="my-3" :value="item" :availableClients="availableClients" :options="options" v-on="cohortListOns(item)" v-for="(item, ind) in pinnedCohorts" :key="`pinned-${ind}`" />
          <CohortsShowMore :count="nextPinnedCohortsIndex" :totalCount="countMorePinnedCohorts" :isLoading="isLoadingPinned" @click="getpinnedPage" v-if="showMorePinnedCohorts" />
        </div>
        <div class="mb-5 mx-4 px-3" v-if="recentCohorts.any() && !cohortsNotFound">
          <div class="hvh-flex space-between mt-4 pt-2">
            <div class="font-18px font-weight-bold">Active Cohorts</div>
          </div>
          <CohortListItem class="my-3" :value="item" :availableClients="availableClients" :options="options" v-on="cohortListOns(item)" v-for="(item, ind) in recentCohorts" :key="`recent-${ind}`" />
          <CohortsShowMore :class="{ disabled: isLoadingRecent }" :count="recentCohorts.length" :totalCount="countMoreRecentCohorts" :isLoading="isLoadingRecent" @click="getRecentData" v-if="showMoreRecentCohorts" />
        </div>
      </div>
    </div>

    <HvhModal title="Cohort Build Started" :value="true" @input="closeModal" v-if="isLoadingCohortCreate">
      <template v-slot>
        <p>We are applying your patient definition to billions of records to find the patients you are looking for. This may require a few minutes.</p>
        <div class="hvh-flex wrap pills">
          <CohortSearchPill class="pill-margin" :item="searchDPDCriteria.first()" />
          <div class="dotdiv" v-if="searchDPDCriteria.length > 1">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </template>
      <template v-slot:actions>
        <v-btn flat class="hvh-btn hvh-btn--primary" @click="closeModal">OK</v-btn>
      </template>
    </HvhModal>
  </ScipherLayout>
</template>

<script>
import axios from '../../../axios';
import CohortListItem from '../../../components/analytics/cohorts/CohortListItem.vue';
import CohortSearchBox from '../../../components/analytics/cohorts/CohortSearchBox.vue';
import CohortSearchPill from '../../../components/analytics/cohorts/CohortSearchPill.vue';
import { createFormData, Debouncer, errorDebug, hashCode, hashToObject, objectToHash } from '../../../libraries/common';
import ScipherLayout from '../../../components/analytics/layout/ScipherLayout.vue';
import CohortsLeftMenu from '../../../components/cohorts/CohortsLeftMenu.vue';
import FixedPlaceholder from '../../../components/common/fixedPlaceholder/FixedPlaceholder.vue';
import HvhModal from '../../../components/common/modal/HvhModal.vue';
import { confirmClinicalVerification, getDemographics } from '../../../libraries/cohorts';
import CohortsShowMore from '../../../components/cohorts/CohortsShowMore.vue';

const perPagePinned = 5;
const perPageRecent = 20;
const perPageBest = 20;

export default {
  title: 'Cohorts',
  components: { CohortListItem, CohortSearchBox, ScipherLayout, FixedPlaceholder, CohortSearchPill, HvhModal, CohortsShowMore },
  data() {
    return {
      pinnedCohorts: [],
      recentCohorts: [],
      recentCohortsBulk: [],
      searchedCohorts: [],
      searchedCohortsBulk: [],
      recentData: [],
      recentFullData: [],
      isLoadingBest: false,
      createSuccess: null,
      isLoadingCohortCreate: false,
      isLoadingPinned: false,
      isLoadingRecent: false,
      isLoadingOptions: false,
      isLoadingSession: false,
      options: {},
      searchFilter: {
        search: [],
        filter: {},
        special: {},
        text: null,
        sortBy: 'lastUpdatedOn',
        ...this.$route.meta.searchFilter,
      },
      debouncer: new Debouncer(1000),
      perPageRecent,
      perPagePinned,
      perPageBest,
      nextPinnedCohortsIndex: 0,
      showMorePinnedCohorts: false,
      countMorePinnedCohorts: 0,
      nextRecentCohortsIndex: 0,
      showMoreRecentCohorts: false,
      countMoreRecentCohorts: 0,
      countMoreBestCohorts: 0,
      nextBestCohortsIndex: 0,
      showMoreBestCohorts: false,
      cohortsNotFound: false,
      CohortsLeftMenu,
      availableClients: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isLoading() {
      return this.isLoadingPinned || this.isLoadingRecent || this.isLoadingBest || this.isLoadingSession;
    },
    bestCohorts() {
      return this.searchedCohorts.filter((s) => s.isBestResult).take(5);
    },
    moreCohorts() {
      return this.searchedCohorts
        .filter((s) => s.isBestResult && !this.bestCohorts.includes(s))
        .concat(this.searchedCohorts.filter((s) => !s.isBestResult));
    },
    resultSelectionTitle() {
      return this.bestCohorts.any() ? 'More Results' : 'Results';
    },
    hasSearchParameters() {
      return this.searchFilter.search.any() || this.searchFilter.text;
    },
    hasAnyCohorts() {
      return this.pinnedCohorts.any() || this.recentCohorts.any() || this.searchedCohorts.any();
    },
    showFilters() {
      return this.hasAnyCohorts && this.hasSearchParameters;
    },
    searchDPDCriteria() {
      return this.searchFilter.search.filter(f => !f.search.toLowerCase().startsWith('tag'));
    },
    currentRouteName() {
      return this.$route.name;
    }
  },
  methods: {
    getOptions() {
      this.isLoadingOptions = true;
      axios
        .get('/Analytics/Cohorts/Options')
        .then(({ data }) => (this.options = data))
        .catch(errorDebug)
        .then(() => (this.isLoadingOptions = false));
    },
    closeModal() {
      this.isLoadingCohortCreate = false;
      window.location.hash = "";
      window.location.reload();
    },
    createCohort() {
      var eventHashMap = this.searchFilter.search
        .filter(f => f.chain)
        .reduce((acc, f) => {
          acc[f.search] = {
            ...f,
            search: undefined,
          };
          return acc;
        }, {});

      var inclusionCriteriaObject = {
        name: 'Inclusion Group',
        children: [
          {
            name: 'Group 1',
            children: [
              {
                occurancesNum: 1,
                separatedByMonths: 1,
                selectedTreeElements: Object.keys(eventHashMap),
                beforeIndexDate: false,
                criterionOperator: 'And',
                thenAfterDays: 0,
                eventHashMap,
                excluded: false
              }
            ],
            excluded: false
          }
        ],
        excluded: false
      };

      var demographics = getDemographics();
      var cohortModel = {
        name: this.searchDPDCriteria.first().label,
        isAgeAtIndexDate: false,

        minAge: demographics.ageRange.min(),
        maxAge: demographics.ageRange.max(),
        minYear: demographics.years.min(),
        maxYear: demographics.years.max(),
        gender: demographics.genders,
        states: demographics.states.map(f => f.value),
        insuranceTypes: [1, 2, 3, 4],
        timeBeforeCriteria: 0,
        timeAfterCriteria: 0,
        enrollmentTime: 0,
        patientDatasource: 1,
        versionGroupId: -1,
        selectedCohort: -1,
        inclusionCriteria: JSON.stringify(inclusionCriteriaObject),
      }
      axios
        .post('/Analytics/Cohorts/CreateCohort', cohortModel)
        .then((response) => {
          this.createSuccess = response.data;

        })
        .catch(err => {
          errorDebug(err);
          this.isLoadingCohortCreate = false;
        });
      this.isLoadingCohortCreate = true;
    },
    getpinnedPage() {
      if (this.currentRouteName == "cohortsPinned") {
        this.getPinnedData();
      }
      else {
        return this.$router.push({ name: 'cohortsPinned' });
      }
    },

    getData() {
      this.nextPinnedCohortsIndex = 0;
      this.nextRecentCohortsIndex = 0;
      return Promise.all([this.getPinnedData(), this.getRecentData()])
        .then(response => {
          this.searchedCohorts = []
          return response.flatMap(f => f);
        });
    },
    getPinnedData() {
      if (this.getFlag('cohorts-show-pinned')) {
        this.isLoadingPinned = true;
        if (this.currentRouteName == "cohortsPinned") {
          this.perPagePinned = 20;
        }
        else {
          this.perPagePinned = 5;
        }
        var skip = this.nextPinnedCohortsIndex ? this.nextPinnedCohortsIndex : 0;
        return axios.post('/Analytics/Cohorts/GetPinned', { ...this.searchFilter, skip, take: this.perPagePinned })
          .then(response => {
            this.pinnedCohorts = [...this.pinnedCohorts, ...response.data.cohorts];
            this.nextPinnedCohortsIndex += response.data.cohorts.length;
            this.showMorePinnedCohorts = response.data.count > this.nextPinnedCohortsIndex;
            this.countMorePinnedCohorts = response.data.count;

            this.triggerScrollSave();
            this.isLoadingPinned = false;
            return this.pinnedCohorts;
          })
          .catch(err => {
            errorDebug(err);
            this.isLoadingPinned = false;
          });
      }
      return Promise.resolve();
    },
    getRecentData() {
      this.isLoadingRecent = true;
      var pinnedInsideRecent = this.recentCohorts.sum((f) => (f.isPinned ? 1 : 0));
      var skip = Math.max(this.recentCohortsBulk.length - pinnedInsideRecent, 0);
      var shouldPreloadMore = !skip;
      var take = this.perPageRecent + (shouldPreloadMore ? perPageRecent : 0);
      if (this.recentCohortsBulk.any())
        this.recentCohorts = [...this.recentCohortsBulk];

      return axios
        .post('/Analytics/Cohorts/GetRecent', { ...this.searchFilter, skip, take })
        .then(({ data }) => {
          if (data.cohorts != null) {
            this.recentCohortsBulk = [...this.recentCohortsBulk, ...data.cohorts];
            this.nextRecentCohortsIndex += this.perPageRecent;
            this.countMoreRecentCohorts = data.count;
            this.showMoreRecentCohorts = this.countMoreRecentCohorts > this.nextRecentCohortsIndex;
            this.recentCohorts = this.recentCohortsBulk.take(this.nextRecentCohortsIndex);
          }
          this.triggerScrollSave();
          this.isLoadingRecent = false;
          return this.recentCohorts;
        })
        .catch(err => {
          errorDebug(err);
          this.isLoadingRecent = false;
        });
    },
    searchChanged(event) {
      if (hashCode(event) !== hashCode(this.searchFilter)) {
        this.searchFilter = event;
        this.nextBestCohortsIndex = 0;
        this.searchedCohortsBulk = [];

        this.debouncer.clear().push(this.runSearch);
      }
    },
    runSearch() {
      this.cohortsNotFound = false;
      var promise = this.hasSearchParameters ? this.getBestResults() : this.getData();
      return promise
        .then(response => {
          axios.post('/WebSession', this.searchFilter)
            .then(({ data }) => window.location.hash = objectToHash({ ...hashToObject(window.location.hash), sessionId: data }));

          if (window.Piwik) {
            var searchFilter = {
              ...this.searchFilter,
              search: this.searchFilter.search.map(f => f.chain),
            };
            var searchFilters = Object.keys(searchFilter)
              .filter(key => JSON.stringify(searchFilter[key] || []).length > 2)
              .reduce((acc, key) => { acc[key] = searchFilter[key]; return acc; }, {});

            if (Object.keys(searchFilters).filter(f => f !== 'sortBy').any()) {
              var tracker = window.Piwik.getTracker();
              tracker.trackSiteSearch(JSON.stringify(searchFilters), 'Cohorts Search', response.length);
            }
          }
        })
    },
    getBestResults() {
      this.isLoadingBest = true;
      var skip = this.searchedCohortsBulk.length;
      var shouldPreloadBest = !skip;
      var take = this.perPageBest + (shouldPreloadBest ? perPageBest : 0);
      if (this.searchedCohortsBulk.any())
        this.searchedCohorts = [...this.searchedCohortsBulk];

      return axios
        .post('/Analytics/Cohorts/GetBestResults', { ...this.searchFilter, skip, take })
        .then(({ data }) => {
          this.searchedCohortsBulk = [...this.searchedCohortsBulk, ...data.cohorts];
          this.nextBestCohortsIndex += this.perPageBest;
          this.countMoreBestCohorts = data.count;
          this.showMoreBestCohorts = this.nextBestCohortsIndex < this.countMoreBestCohorts;
          this.searchedCohorts = this.searchedCohortsBulk.take(this.nextBestCohortsIndex);
          this.cohortsNotFound = !this.searchedCohorts.any();

          if (!this.cohortsNotFound) {
            this.pinnedCohorts = [];
            this.recentCohorts = [];
            this.recentCohortsBulk = [];

            this.triggerScrollSave();
          }
          this.isLoadingBest = false;
          return this.searchedCohorts;
        })
        .catch(err => {
          errorDebug(err);
          this.isLoadingBest = false;
        });
    },
    cohortListOns(item) {
      return {
        'pin-cohort': this.pinCohort.bind(this, item),
        archive: this.archive.bind(this, item),
        'archive-all-versions': this.archiveAllVersions.bind(this, item),
        'copy-url': () => this.copyUrl(item),
        'hide-cohort': this.hideCohort.bind(this, item),
        'clinically-verify': this.clinicallyVerify.bind(this, item),
        'ask-clinically-verify': this.askClinicallyVerify.bind(this, item),
        'new-analysis': this.newAnalysis.bind(this, item),
        'input': this.cohortEdited.bind(this, item),
        publish: this.publish.bind(this, item),
      };
    },
    copyUrl(item) {
      var route = this.$router.resolve({ name: 'cohort', params: { id: item.id } });
      var url = location.origin + route.href;
      navigator.clipboard.writeText(url);
      this.$notify('Url copied to the clipboard!');
    },
    pinCohort(item, shouldPin) {
      this.isLoadingPinned = true;
      axios
        .post(`/Analytics/Cohorts/${item.id}/PinCohort`, shouldPin)
        .then(() => {
          this.$set(item, 'isPinned', shouldPin);
          if (this.searchedCohorts.includes(item)) {
            this.searchedCohorts = [...this.searchedCohorts];
          }
        })
        .catch(errorDebug)
        .then(() => (this.isLoadingPinned = false));
    },
    clinicallyVerify(item, shouldVerify) {
      this.isLoadingPinned = true;
      axios
        .post(`/Analytics/Cohorts/${item.id}/ClinicallyVerify`, shouldVerify)
        .then(({ data }) => {
          this.$set(item, 'cohortVerified', shouldVerify ? data : null);
          this.$set(item, 'cohortVerificationPending', false);
          if (this.searchedCohorts.includes(item)) {
            this.searchedCohorts = [...this.searchedCohorts];
          }
        })
        .catch(errorDebug)
        .then(() => (this.isLoadingPinned = false));
    },
    askClinicallyVerify(item) {
      confirmClinicalVerification.call(this)
        .then(() => {
          this.isLoadingPinned = true;
          axios
            .post(`/Analytics/Cohorts/${item.id}/AskClinicallyVerification`)
            .then(() => {
              this.$set(item, 'cohortVerificationPending', true);
              if (this.searchedCohorts.includes(item)) {
                this.searchedCohorts = [...this.searchedCohorts];
              }
            })
            .catch(errorDebug)
            .then(() => (this.isLoadingPinned = false));
        })
        .catch(() => null);
    },
    hideCohort(item, shouldHide) {
      this.isLoadingPinned = true;
      axios
        .post(`/Analytics/Cohorts/${item.id}/HideCohort`, shouldHide)
        .then(() => {
          this.$set(item, 'isPrivate', shouldHide);
          if (this.searchedCohorts.includes(item)) {
            this.searchedCohorts = [...this.searchedCohorts];
          }
        })
        .catch(errorDebug)
        .then(() => (this.isLoadingPinned = false));
    },
    getScrollData() {
      return {
        nextPinnedCohortsIndex: this.nextPinnedCohortsIndex,
        nextRecentCohortsIndex: this.nextRecentCohortsIndex,
        nextBestCohortsIndex: this.nextBestCohortsIndex,
      };
    },
    triggerScrollSave() {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    },
    loadInitialData(initialScrollData) {
      this.cohortsNotFound = false;
      var perPageRecent = this.perPageRecent;
      var perPagePinned = this.perPagePinned;
      var perPageBest = this.perPageBest;
      this.perPageRecent = initialScrollData.nextRecentCohortsIndex || perPageRecent
      this.perPagePinned = initialScrollData.nextPinnedCohortsIndex || perPagePinned;
      this.perPageBest = initialScrollData.nextBestCohortsIndex || perPageBest;

      if (!initialScrollData?.sessionId) {
        return this.getData()
          .catch(errorDebug)
          .then(() => {
            this.perPageRecent = perPageRecent;
            this.perPagePinned = perPagePinned;
            this.perPageBest = perPageBest;
          });
      } else {
        this.isLoadingSession = true;
        return axios.get(`/WebSession/${initialScrollData.sessionId}`)
          .then(({ data }) => {
            this.searchFilter = { ...this.searchFilter, ...data };
            return this.runSearch();
          })
          .catch(err => {
            this.runSearch();
            errorDebug(err);
          })
          .then(() => {
            this.perPageRecent = perPageRecent;
            this.perPageBest = perPageBest;
            this.isLoadingSession = false;
          });
      }
    },
    archive(item) {
      this.isLoadingPinned = true;
      axios
        .post(`/Analytics/Cohort/Archive`, createFormData({ cohortIds: [item.id] }))
        .then(() => this.removeItem(item))
        .catch(errorDebug)
        .then(() => this.isLoadingPinned = false);
    },
    archiveAllVersions(item) {
      this.isLoadingPinned = true;
      axios
        .post(`/Analytics/Cohort/ArchiveAllVersions`, createFormData({ versionGroupIds: [item.versionGroupId] }))
        .then(() => {
          [...this.pinnedCohorts, ...this.recentCohorts, ...this.searchedCohorts]
            .filter(f => f.versionGroupId === item.versionGroupId)
            .forEach(this.removeItem);
        })
        .catch(errorDebug)
        .then(() => this.isLoadingPinned = false);
    },
    removeItem(item) {
      if (this.pinnedCohorts.includes(item)) {
        this.pinnedCohorts = this.pinnedCohorts.filter(f => f !== item);
        this.nextPinnedCohortsIndex -= 1;
      }
      if (this.recentCohorts.includes(item)) {
        this.recentCohorts = this.recentCohorts.filter(f => f !== item);
        this.nexRecentCohortsIndex -= 1;
      }
      if (this.searchedCohorts.includes(item)) {
        this.searchedCohorts = this.searchedCohorts.filter(f => f !== item);
        this.nextBestCohortsIndex -= 1;
      }
    },
    newAnalysis(item) {
      this.goTo(`/Analytics/Cohort/${item.id}/Run`);
    },
    cohortEdited(item, event) {
      var usedCollection = ['pinnedCohorts', 'recentCohorts', 'searchedCohorts']
        .find(key => this[key].indexOf(item) !== -1);
      this[usedCollection] = [...this[usedCollection]]
        .replaceAt(this[usedCollection].indexOf(item), event);
    },
    publish(item, clientsToPublish) {
      this.isLoadingPinned = true;
      return axios.post(`/Analytics/Cohorts/${item.id}/Publish`, [...clientsToPublish])
        .then((response) => {
          this.$set(item, 'clientsPublishedView', [...response.data]);
        })
        .catch(errorDebug)
        .then(() => this.isLoadingPinned = false
        );
    },
  },
  watch: {
    '$route.name'() {
      this.pinnedCohorts = [];
      this.recentCohorts = [];
      this.searchFilter = [];
      this.recentCohortsBulk = [];
      this.searchedCohortsBulk = [];
      this.nextPinnedCohortsIndex = 0;
      this.nextRecentCohortsIndex = 0;
      this.nextBestCohortsIndex = 0;
      this.cohortsNotFound = false;

      this.debouncer.clear();
      this.searchFilter = { ...this.searchFilter, ...this.$route.meta.searchFilter };
      this.runSearch();
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisCohorts'] = this;

    if (!this.isLoading) {
      this.getData();
    }
    this.getOptions();
    axios.get('/Account/GetAvailableClients')
      .then(({ data }) => {
        var clientId = this.$store.state.user.client.id;
        this.availableClients = data.clients.filter(t => t.id != clientId);
      });
  },
};
</script>

<style lang="scss" scoped>
.dotdiv {
  text-align: center;
  width: 30px;
  height: 25px;
  border-radius: 3px;
}
.dot {
  height: 5px;
  width: 5px;
  background-color: #000000;
  margin-left: 2px;
  border-radius: 50%;
  display: inline-block;
  font-weight: bold;
}

.cohorts-container {
  margin: auto;
}

@media only screen and (min-width: 1264px) {
  .cohorts-container {
    max-width: 1200px;
  }
}
</style>